import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  DestinationPlatformData,
  FACEBOOK_CONTENT_CATEGORIES,
  FACEBOOK_PUBLISH_TARGETS,
  FacebookDestinationPlatformData,
  INSTAGRAM_DEFAULT_POST_TYPE,
  INSTAGRAM_POST_TYPES,
  InstagramDestinationPlatformData,
  TikTokDestinationPlatformData,
} from '../../../domain/destinations';
import { DestinationSettingsAppearance } from './destination-settings-panel.component';

/** Dropdown sentinel for unset Facebook content category (maps to `null` in API). */
const FACEBOOK_CONTENT_CATEGORY_NONE = '';
const FACEBOOK_PAGE_VIDEO = 'PageVideo';
const FACEBOOK_PAGE_REEL = 'PageReel';

@Component({
  selector: 'evrideo-destination-platform-settings',
  templateUrl: './destination-platform-settings.component.html',
  styleUrls: ['./destination-platform-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DestinationPlatformSettingsComponent implements OnChanges {
  @Input() platformId = 0;
  @Input() platformData: DestinationPlatformData | null = null;
  @Input() readonly = false;
  @Input() appearance: DestinationSettingsAppearance = 'default';

  @Output() platformDataChange = new EventEmitter<DestinationPlatformData>();

  readonly facebookPublishTargets = FACEBOOK_PUBLISH_TARGETS;
  readonly facebookContentCategories = FACEBOOK_CONTENT_CATEGORIES;
  readonly facebookContentCategoryNone = FACEBOOK_CONTENT_CATEGORY_NONE;
  readonly instagramPostTypes = INSTAGRAM_POST_TYPES;
  readonly facebookPageVideoOnlyTooltip =
    'Applies to Page video uploads only. Not used for Reels.';
  readonly instagramShareToFeedTooltip =
    'Applies to Reels only. Not used for Story.';

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  privacyLevel: string | null = null;
  disableComment = false;
  disableDuet = false;
  disableStitch = false;
  publishMode: string | null = null;

  defaultPostType: string = INSTAGRAM_DEFAULT_POST_TYPE;
  shareToFeed = true;
  isAiGenerated = false;

  defaultPublishTarget: string | null = null;
  reelsEligible = false;
  embeddable = false;
  noStory = false;
  contentCategory: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['platformData'] || changes['platformId']) {
      this.syncFromPlatformData();
    }
  }

  onTikTokCheckboxChange(
    field: 'disableComment' | 'disableDuet' | 'disableStitch',
    checked: boolean,
  ): void {
    if (this.readonly) {
      return;
    }
    this[field] = checked;
    this.emitPlatformData();
  }

  onInstagramDefaultPostTypeChange(value: string): void {
    if (this.readonly) {
      return;
    }
    this.defaultPostType = value;
    this.emitPlatformData();
  }

  onInstagramShareToFeedChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.shareToFeed = checked;
    this.emitPlatformData();
  }

  onInstagramIsAiGeneratedChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.isAiGenerated = checked;
    this.emitPlatformData();
  }

  onFacebookPublishTargetChange(value: string): void {
    if (this.readonly || !this.isFacebookPublishTargetSelectable(value)) {
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.defaultPublishTarget = value;
    this.emitPlatformData();
  }

  onFacebookReelsEligibleChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.reelsEligible = checked;
    if (!checked && this.defaultPublishTarget === FACEBOOK_PAGE_REEL) {
      this.defaultPublishTarget = FACEBOOK_PAGE_VIDEO;
    }
    this.emitPlatformData();
  }

  isFacebookPublishTargetSelectable(value: string): boolean {
    if (value !== FACEBOOK_PAGE_REEL) {
      return true;
    }
    return this.reelsEligible;
  }

  onFacebookEmbeddableChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.embeddable = checked;
    this.emitPlatformData();
  }

  onFacebookNoStoryChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.noStory = checked;
    this.emitPlatformData();
  }

  onFacebookContentCategoryChange(value: string): void {
    if (this.readonly) {
      return;
    }
    this.contentCategory =
      value === FACEBOOK_CONTENT_CATEGORY_NONE ? null : value;
    this.emitPlatformData();
  }

  get facebookContentCategorySelection(): string {
    return this.contentCategory ?? FACEBOOK_CONTENT_CATEGORY_NONE;
  }

  private syncFromPlatformData(): void {
    const data = this.platformData;
    if (!data) {
      return;
    }

    switch (this.platformId) {
      case 2: {
        const tiktok = data as TikTokDestinationPlatformData;
        this.privacyLevel = tiktok.privacy_level;
        this.disableComment = tiktok.disable_comment ?? false;
        this.disableDuet = tiktok.disable_duet ?? false;
        this.disableStitch = tiktok.disable_stitch ?? false;
        this.publishMode = tiktok.publish_mode;
        break;
      }
      case 3: {
        const ig = data as InstagramDestinationPlatformData;
        this.defaultPostType =
          ig.default_post_type ?? INSTAGRAM_DEFAULT_POST_TYPE;
        this.shareToFeed = ig.share_to_feed ?? true;
        this.isAiGenerated = ig.is_ai_generated ?? false;
        break;
      }
      case 4: {
        const fb = data as FacebookDestinationPlatformData;
        this.reelsEligible = fb.reels_eligible ?? false;
        this.defaultPublishTarget =
          fb.default_publish_target ?? FACEBOOK_PAGE_VIDEO;
        if (
          !this.reelsEligible &&
          this.defaultPublishTarget === FACEBOOK_PAGE_REEL
        ) {
          this.defaultPublishTarget = FACEBOOK_PAGE_VIDEO;
        }
        this.embeddable = fb.embeddable ?? false;
        this.noStory = fb.no_story ?? false;
        this.contentCategory = fb.content_category ?? null;
        break;
      }
      default:
        break;
    }
  }

  private emitPlatformData(): void {
    switch (this.platformId) {
      case 2:
        this.platformDataChange.emit({
          privacy_level: this.privacyLevel,
          disable_comment: this.disableComment,
          disable_duet: this.disableDuet,
          disable_stitch: this.disableStitch,
          publish_mode: this.publishMode,
        });
        break;
      case 3:
        this.platformDataChange.emit({
          default_post_type: this.defaultPostType,
          share_to_feed: this.shareToFeed,
          is_ai_generated: this.isAiGenerated,
        });
        break;
      case 4:
        this.platformDataChange.emit({
          default_publish_target: this.defaultPublishTarget,
          reels_eligible: this.reelsEligible,
          embeddable: this.embeddable,
          no_story: this.noStory,
          content_category: this.contentCategory,
        });
        break;
      default:
        break;
    }
    this.changeDetectorRef.markForCheck();
  }
}
